import React, { CSSProperties } from 'react';
import { RUN } from '../content';
import { AFTER_FADE, stageClass, usePlayOnce } from '../motion';

// One support-agent run on one line. Five steps pick from options the agent already has: each
// is a bead in its kind's colour, with its answer and probability under it. The reply is
// written a token at a time, so its step is a run of ticks, one for each token. On its moment
// the line draws itself from step to step and each pick lands as the line reaches it; at the
// reply the line waits while the ticks fill in one by one, the one step that takes its time.
// On a narrow screen the run goes down the page, one step a row.

const W = 1104;
const H = 120;
const Q_Y = 15;
const LINE_Y = 50;
const A_Y = 92;
const P_Y = 113;
// The end stations sit so their questions come close to the column's edges, level with the
// header on the left and the legend on the right.
const X_FIRST = 54;
const X_LAST = 1030;
const PITCH = (X_LAST - X_FIRST) / (RUN.length - 1);
const station = (i: number) => X_FIRST + i * PITCH;

const TICKS = 14;
const TICK_STEP = 9;
const TICK_W = 5;
const TICK_H = 12;
const HALF = ((TICKS - 1) * TICK_STEP + TICK_W) / 2; // half the ticks' width
const CLEAR = 8; // between the line and the ticks
const LIST_TICKS = 5;

const HOP = 0.55; // seconds for the line to cross from one pick to the next
const TICK = 0.16; // seconds from one tick to the next

const writes = (i: number) => RUN[i].kind === 'write';
const left = (i: number) => (writes(i) ? station(i) - HALF - CLEAR : station(i));
const right = (i: number) => (writes(i) ? station(i) + HALF + CLEAR : station(i));

// The line's stretches between the steps, when each step lands and when each tick fills, in
// seconds from the start of the moment.
function timeline() {
  const stretches: { x1: number; x2: number; at: number; dur: number }[] = [];
  const lands: number[] = [];
  const ticks: number[] = [];
  let t = 0;
  RUN.forEach((_, i) => {
    if (i > 0) {
      const x1 = right(i - 1);
      const x2 = left(i);
      const dur = (HOP * (x2 - x1)) / PITCH;
      stretches.push({ x1, x2, at: t, dur });
      t += dur;
    }
    if (writes(i)) {
      for (let k = 0; k < TICKS; k += 1) ticks.push(t + k * TICK);
      t += TICKS * TICK;
    }
    lands.push(t);
  });
  return { stretches, lands, ticks };
}

const { stretches: STRETCHES, lands: LANDS, ticks: TICK_AT } = timeline();
// The narrow run's ticks fill over the same time as the wide run's.
const LIST_TICK_AT = Array.from({ length: LIST_TICKS }, (_, k) => TICK_AT[0] + (k * TICKS * TICK) / LIST_TICKS);

const vars = (v: Record<string, string | number>) => v as CSSProperties;
const at = (t: number) => `${(AFTER_FADE + t).toFixed(2)}s`;

function Strip() {
  const [ref, stage] = usePlayOnce<SVGSVGElement>();
  return (
    <svg
      ref={ref}
      className={`oh-fig oh-run ${stageClass(stage)}`}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="One support-agent run: five of its six steps pick from options the agent already has, each with a probability, and one writes the reply, token by token."
    >
      {STRETCHES.map((s) => (
        <line
          key={s.x1}
          className="oh-run-seg"
          x1={s.x1}
          y1={LINE_Y}
          x2={s.x2}
          y2={LINE_Y}
          style={vars({ '--len': (s.x2 - s.x1).toFixed(1), '--d': at(s.at), '--dur': `${s.dur.toFixed(2)}s` })}
        />
      ))}
      {RUN.map((step, i) => {
        const x = station(i);
        return (
          <g key={step.question} className={`oh-kind-${step.kind}`} style={vars({ '--d': at(LANDS[i]) })}>
            <text className="oh-run-q" x={x} y={Q_Y} textAnchor="middle">{step.question}</text>
            {step.kind === 'write' ? (
              <>
                {TICK_AT.map((t, k) => (
                  <rect
                    key={k}
                    className="oh-run-tick"
                    x={x - HALF + k * TICK_STEP}
                    y={LINE_Y - TICK_H / 2}
                    width={TICK_W}
                    height={TICK_H}
                    rx="1.5"
                    style={vars({ '--d': at(t) })}
                  />
                ))}
                <text className="oh-run-out oh-run-how" x={x} y={A_Y} textAnchor="middle">{step.answer}</text>
              </>
            ) : (
              <>
                <circle className="oh-run-bead" cx={x} cy={LINE_Y} r="8" />
                <g className="oh-run-out">
                  <text className="oh-run-a" x={x} y={A_Y} textAnchor="middle">{step.answer}</text>
                  <text className="oh-run-p" x={x} y={P_Y} textAnchor="middle">{step.p?.toFixed(2)}</text>
                </g>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function List() {
  const [ref, stage] = usePlayOnce<HTMLOListElement>();
  return (
    <ol ref={ref} className={`oh-run-list ${stageClass(stage)}`} aria-label="One support-agent run">
      {RUN.map((step, i) => (
        <li key={step.question} className={`oh-kind-${step.kind}`} style={vars({ '--d': at(LANDS[i]) })}>
          <span className="oh-run-q">{step.question}</span>
          {step.kind === 'write' ? (
            <span className="oh-run-mark oh-run-ticks" aria-hidden="true">
              {LIST_TICK_AT.map((t) => <i key={t} style={vars({ '--d': at(t) })} />)}
            </span>
          ) : (
            <span className="oh-run-mark" aria-hidden="true" />
          )}
          <span className="oh-run-a">
            {step.kind === 'write' ? (
              <span className="oh-run-how">{step.answer}</span>
            ) : (
              <>
                <span>{step.answer}</span>
                <span className="oh-run-p">{step.p?.toFixed(2)}</span>
              </>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function AgentRun() {
  return (
    <figure className="oh-figure">
      <div className="hidden lg:block"><Strip /></div>
      <div className="lg:hidden"><List /></div>
      <figcaption className="oh-caption-row oh-run-caption">
        <span className="oh-legend">
          <span><i className="oh-kind-single" />one of several</span>
          <span><i className="oh-kind-binary" />yes or no</span>
          <span><i className="oh-kind-write" />writes</span>
        </span>
      </figcaption>
    </figure>
  );
}

export default AgentRun;
